import React from "react";
import { ScheduleConflictItem, ScheduleEntryType } from "@myapp/contract";

/**
 * The list of blocking entries shown by ConflictDialog and BlockerDialog.
 *
 * Both dialogs render the same `ScheduleConflictItem[]` — only the surrounding copy and the
 * buttons differ — so the row markup and the date formatting live here instead of being kept in
 * sync by hand in two files.
 */

const ENTRY_TYPE_LABELS: Record<ScheduleEntryType, string> = {
  APPOINTMENT: "Termin",
  TASK: "Aufgabe",
  ABSENCE: "Abwesenheit",
  BREAK: "Pause",
};

/**
 * Why this entry blocks the slot. A break with no `entryId` was never stored — it is projected
 * from the technician profile — and saying so keeps the user from looking for it in the calendar.
 */
export function getConflictReason(conflict: ScheduleConflictItem): string {
  if (conflict.type === "BREAK" && conflict.entryId === null) return "Pause (Standard)";
  return ENTRY_TYPE_LABELS[conflict.type];
}

export function formatConflictRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const fmtDate = (d: Date) =>
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const sameDay = fmtDate(start) === fmtDate(end);
  return sameDay
    ? `${fmtDate(start)} ${fmtTime(start)} – ${fmtTime(end)}`
    : `${fmtDate(start)} ${fmtTime(start)} – ${fmtDate(end)} ${fmtTime(end)}`;
}

type Props = {
  items: ScheduleConflictItem[];
};

export const ConflictItemList: React.FC<Props> = ({ items }) => (
  <div className="flex-1 overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
    {items.map((item, i) => (
      <div
        key={`${item.type}:${item.entryId ?? "projected"}:${item.technicianId}:${item.startsAt}`}
        className={`flex items-start justify-between gap-2 px-3 py-2 text-sm ${
          i > 0 ? "border-t border-gray-200" : ""
        }`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="flex-shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
              {getConflictReason(item)}
            </span>
            <p className="truncate font-medium text-gray-800">{item.label}</p>
          </div>
          <p className="text-xs text-gray-500">{item.technicianName}</p>
        </div>
        <span className="flex-shrink-0 text-xs text-gray-500">
          {formatConflictRange(item.startsAt, item.endsAt)}
        </span>
      </div>
    ))}
  </div>
);

export default ConflictItemList;
